import { SubmissionStatus } from '@bp/bp-graphql-types';
import { ArrowDownIcon, ArrowUpIcon, Button } from '@bp/ui-components';
import classNames from 'classnames';
import { GanttFlyout } from 'components/GanttFlyout/GanttFlyout';
import { getSubmissionStatusTranslation } from 'components/Submissions/utils/submissionUtils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GanttAssignment, GanttBar, GanttDay } from '../OpenAssignmentsOverview/OpenAssignmentsOverview';
import styles from './AssignmentsGantt.module.scss';

type AssignmentsGanttProps = {
  timespan: GanttDay[];
  assignments: GanttAssignment[];
  mode: 'desktop' | 'tablet' | 'mobile';
  context: 'home' | 'course';
  daysToShow?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  onNavigate: (courseUuid: string, uuid: string) => void;
};

export const AssignmentsGantt: FC<AssignmentsGanttProps> = ({
  timespan,
  assignments,
  mode = 'desktop',
  context,
  daysToShow = 7,
  onNavigate,
}) => {
  const { t } = useTranslation();

  const [showHidden, setShowHidden] = useState(false);

  const rowThreshold = mode === 'desktop' ? 4 : 6;

  function getBarClasses(bar: GanttBar, status: SubmissionStatus | undefined) {
    return classNames(
      styles.bar,
      {
        [styles.start]: bar === 1 || bar === 4,
        [styles.end]: bar === 3 || bar === 4,
      },
      styles['status-' + status?.toLowerCase()],
    );
  }

  function getKeyFromDay(day: GanttDay): string {
    const date = day.dayMonthYear;
    return `${date.day}${date.month}${date.year}`;
  }

  function getStatusText(status: SubmissionStatus | undefined) {
    return status ? getSubmissionStatusTranslation(status, 'student') : '';
  }

  function getMaxWidth(segments: GanttBar[]): string {
    const length = segments.filter((s) => s !== 0).length;
    return `${length * 100}%`;
  }

  const filled: GanttAssignment[] = assignments;
  if (assignments.length < rowThreshold) {
    for (let i = assignments.length; i < rowThreshold; i++) {
      filled.push({
        key: i.toString(),
        courseUuid: '',
      });
    }
  }

  let isEmpty = true;
  filled.forEach((assignment) => {
    if (isNaN(Number(assignment.key.toString()))) {
      isEmpty = false;
      return;
    }
  });

  const classes = classNames(styles['assignments-gantt'], {
    [styles.tablet]: mode === 'tablet',
  });

  function getMouseOverTitle(assignment: GanttAssignment): string {
    if (context === 'home') {
      return `${assignment.subjectName}\n${assignment.title}\n${getStatusText(assignment.status)}`;
    } else {
      return `${assignment.title}\n${getStatusText(assignment.status)}`;
    }
  }

  function getTitle(assignment: GanttAssignment): string {
    if (context === 'home') {
      return `${assignment.subjectShortName} - ${assignment.title}`;
    } else {
      return assignment.title ?? t('assignments.title', { count: 1 });
    }
  }

  return (
    <div className={classes}>
      <div className={classNames(styles.header)}>
        <div className={styles.days}>
          {timespan.map((day) => {
            return (
              <div
                key={getKeyFromDay(day)}
                className={classNames(styles.day, {
                  [styles.active]: day.isToday,
                })}
                style={{ maxWidth: `calc(100% / ${daysToShow})` }}
              >
                {day.events && day.events.length > 0 && (
                  <GanttFlyout
                    trigger={
                      <div
                        className={classNames(styles.event, {
                          [styles.multiple]: day.events.length > 1,
                        })}
                      >
                        <div className={styles.type}>{t('events.categories.event')}</div>
                        {day.events.length > 1 && <div className={styles.badge}>{`+${day.events.length - 1}`}</div>}
                      </div>
                    }
                    entries={day.events ?? []}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className={classNames(styles['assignments-wrapper'])}>
        {filled.map((assignment, assignIndex) => {
          return (
            <div
              key={assignment.key}
              className={classNames(styles.row, {
                [styles.hidden]: assignIndex > rowThreshold - 1 && !showHidden,
              })}
            >
              <div className={styles.days}>
                {timespan.map((day, dayIndex) => {
                  return (
                    <div
                      key={getKeyFromDay(day)}
                      className={classNames(styles.day, {
                        [styles.active]: day.isToday,
                      })}
                      style={{ zIndex: 7 - dayIndex, maxWidth: `calc(100% / ${daysToShow})` }}
                    >
                      {assignment.bars && assignment.bars[dayIndex] !== 0 && (
                        <div
                          className={getBarClasses(assignment.bars[dayIndex], assignment.status)}
                          title={getMouseOverTitle(assignment)}
                          onClick={() => onNavigate(assignment.courseUuid, assignment.key)}
                        >
                          {(assignment.bars[dayIndex] === 1 || assignment.bars[dayIndex] === 4 || dayIndex === 0) && (
                            <div className={styles['bar-text']}>
                              <div
                                className={styles['assignment-title']}
                                style={{
                                  maxWidth: getMaxWidth(assignment.bars),
                                }}
                              >
                                {getTitle(assignment)}
                              </div>
                              <div className={styles['status-text']}>{getStatusText(assignment.status)}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {assignments && assignments.length > rowThreshold && (
        <div className={styles.button}>
          <Button
            hierarchy='primary'
            onClick={() => {
              setShowHidden(!showHidden);
            }}
          >
            <div className={styles.caption}>
              <div>{`${showHidden ? '-' : '+'} ${assignments.length - rowThreshold}`}</div>
              <div>{showHidden ? <ArrowUpIcon /> : <ArrowDownIcon />}</div>
            </div>
          </Button>
        </div>
      )}

      {(!assignments || isEmpty) && (
        <div className={styles.placeholder}>{t('assignments.noAssignmentsInTimespan')}</div>
      )}
    </div>
  );
};
